import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet-async';
import api from '../../Middleware/api/api';
import { useAuth } from '../../services/contexts';
import { useTranslation } from 'react-i18next';
import { Add as AddIcon } from '@mui/icons-material';
import {
  Divider as MuiDivider,
  Grid,
  Button,
  ThemeProvider,
  Typography,
  TableCell,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { spacing } from '@mui/system';
import DataTable from '../../components/DataTable';
import { PAGE_SIZE, MIN_PAGE_SIZE } from '../../services/utils/units-constants';
const Divider = styled(MuiDivider)(spacing);
import RFIDForm from './components/RFIDForm';
import StepperPopup from '../../components/StepperPopup';
import FailFeedback from '../../components/DataTable/Feedback';
import { createTheme } from '@mui/material/styles';
import LogoLoader from '../../components/LogoLoader';
import Feedback from '../../components/Feedback';

// eslint-disable-next-line no-unused-vars
const dataTableTheme = () =>
  createTheme({
    components: {
      MuiList: {
        styleOverrides: {
          root: {
            maxHeight: 300,
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            '&[data-colindex="5"]': {
              '& > *': {
                justifyContent: 'center',
                textAlign: 'center',
              },
            },
            '&[data-colindex="1"]': {
              '& > *': {
                justifyContent: 'left',
                textAlign: 'left',
                paddingLeft: 12,
              },
            },
            '&[data-colindex="6"], &[data-colindex="7"]': {
              '& > *': {
                justifyContent: 'right',
                textAlign: 'right',
                paddingLeft: 12,
              },
            },
          },
        },
      },
      MUIDataTableBodyCell: {
        styleOverrides: {
          root: {
            '&[data-colindex="4"]': {
              '& > *': {
                justifyContent: 'center',
                textAlign: 'center',
                paddingRight: 10,
              },
            },
          },
        },
      },
    },
  });

function RFIDS() {
  const { t } = useTranslation();

  const { token } = useAuth();
  const [rfIds, setRFIDs] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE[0]);
  const [fetchingData, setFetchingData] = React.useState(true);
  const [open, setOpen] = useState(false);
  const [submitOperator, setSubmitOperator] = useState(0);
  // declaring states for the betreiber anlegen form
  const [canNextOperator, setCanNextOperator] = useState(false);
  const [error, setError] = useState(false);
  const [duplicateRfids, setDuplicateRfids] = useState([]);
  const [success, setSuccess] = useState(false);
  const [newFilters, setNewFilters] = useState([]);
  const [enterKeyboardButtonPressed, setEnterKeyboardButtonPressed] =
    useState(false);

  const buildUrlParams = useMemo(() => {
    const urlParams = new URLSearchParams();
    if (newFilters && typeof newFilters === 'object') {
      Object.entries(newFilters).forEach(([key, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          urlParams.set(key, values.join(','));
        }
      });
    }
    urlParams.set('page', currentPage);
    urlParams.set('size', pageSize);
    return urlParams;
  }, [newFilters, currentPage, pageSize]);

  const fetchRfIdsList = () => {
    getAllRfIds(buildUrlParams);
  };

  const handleApply = () => {
    if (currentPage > 0) {
      setCurrentPage(0);
    } else {
      fetchRfIdsList();
    }
    setEnterKeyboardButtonPressed(false);
  };

  useEffect(async () => {
    fetchRfIdsList();
  }, [currentPage, pageSize]);

  const getAllRfIds = async (urlParams) => {
    setRFIDs([]);
    setFetchingData(true);
    try {
      api.getAllRFIDs(token, urlParams).then(
        (response) => {
          if (response?.data?.data == null) {
            setFetchingData(false);
            setRFIDs([]);
          } else {
            setFetchingData(false);
            setTotalRecords(response?.data?.data?.totalNumberOfRecords);
            setRFIDs(response?.data?.data?.response);
          }
        },
        () => {
          setFetchingData(false);
          setRFIDs([]);
        }
      );
    } catch (exception) {
      setFetchingData(false);
      setRFIDs([]);
    }
  };

  const handleEnterKey = React.useCallback((e) => {
    console.log('e?.key value ', e?.key);
    if (e?.key === 'Enter') {
      e.preventDefault();
      setEnterKeyboardButtonPressed(true);
    }
  }, []);

  useEffect(() => {
    if (enterKeyboardButtonPressed) {
      handleApply();
    }
  }, [enterKeyboardButtonPressed]);

  const resetFilterApi = () => {
    const resetParams = new URLSearchParams();
    resetParams.set('page', '0');
    resetParams.set('size', pageSize.toString());
    getAllRfIds(resetParams);
  };

  // Define columns
  const columns = useMemo(() => [
    {
      name: 'serialId',
      label: t('LBLColumnRfidSerialNr'),
      options: {
        filter: true,
        filterOptions: {
          fullWidth: true,
        },
        sort: false,
        customFilterListRender: (v) => t('LBLColumnRfidSerialNr') + `: ${v}`,
        filterType: 'textField',

        customBodyRender: (value) => {
          return <Typography align="left">{value}</Typography>;
        },
        customHeadRender: () => (
          <TableCell
            align="left"
            variant="head"
            sx={{
              textAlign: 'left',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            {t('LBLColumnRfidSerialNr')}
          </TableCell>
        ),
        setCellHeaderProps: () => {
          return { align: 'start' };
        },
        setCellProps: () => {
          return { align: 'left' };
        },
      },
    },
    {
      name: 'rfidTag',
      label: t('LBLColumnRfidTag'),
      options: {
        filter: true,
        filterOptions: {
          fullWidth: true,
        },
        sort: false,
        customFilterListRender: (v) => t('LBLColumnRfidTag') + `: ${v}`,
        filterType: 'textField',
        customBodyRender: (value) => {
          return <Typography align="left">{value}</Typography>;
        },
        customHeadRender: () => (
          <TableCell
            align="left"
            variant="head"
            sx={{
              textAlign: 'left',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
            }}
          >
            {t('LBLColumnRfidTag')}
          </TableCell>
        ),
        setCellHeaderProps: () => {
          return { align: 'start' };
        },
        setCellProps: () => {
          return { align: 'left' };
        },
      },
    },
  ]);

  return (
    <React.Fragment>
      <Helmet title="Invoices" />
      <LogoLoader loading={fetchingData} />
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            {t('LBLRFIDMgmt')}
          </Typography>
        </Grid>
        <Grid item mb={6}>
          <Button
            spacing={1}
            container
            item
            xs={12}
            sx={{ marginLeft: 5 }}
            variant="contained"
            color="primary"
            onClick={() => {
              setOpen(true);
            }}
          >
            <AddIcon />
            {t('BTNAddRFID')}
          </Button>
        </Grid>
      </Grid>
      <RFIDForm
        isOpen={open}
        handleClose={() => setOpen(false)}
        submit={submitOperator}
        setPayload={async (val) => {
          setFetchingData(true);
          try {
            api.createRFID(token, val).then(
              (response) => {
                setOpen(false);
                setSuccess(true);
                fetchRfIdsList();
              },
              (err) => {
                setFetchingData(false);
                setOpen(false);
                const apiError = err?.response?.data?.error;
                if (apiError?.errorCodeResponse === 'RFID_ALREADY_EXISTS') {
                  setDuplicateRfids(apiError.errorDetail || []);
                }
                setError(true);
              }
            );
          } catch (exception) {
            setFetchingData(false);
            setOpen(false);
            setError(true);
          }
        }}
        setCanNext={setCanNextOperator}
        setSubmit={setSubmitOperator}
      />
      <Divider my={6} />
      <ThemeProvider
        theme={(appTheme) => ({
          ...appTheme,
          components: {
            ...appTheme.components,
            ...dataTableTheme(appTheme).components,
          },
        })}
      >
        <DataTable
          data={rfIds}
          columns={columns}
          // options={options}
          options={{
            textLabels: {
              body: {
                noMatch: fetchingData ? t('LBLLOading') : t('LBLNoData'),
              },
            },
            filter: true,
            currentPage: currentPage,
            totalRecords: totalRecords,
            selectableRows: 'none',

            customFilterDialogFooter: () => {
              return (
                <div style={{ margin: '1rem', textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApply}
                  >
                    {t('BTNSubmit')}
                  </Button>
                </div>
              );
            },
            onFilterDialogOpen: () => {
              window.addEventListener('keydown', handleEnterKey);
            },
            onFilterDialogClose: () => {
              window.removeEventListener('keydown', handleEnterKey);
            },
            onTableChange: (action, tableState) => {
              if (action === 'filterChange') {
                const newFilters = {};
                tableState.filterList.forEach((filter, index) => {
                  if (filter.length > 0) {
                    newFilters[tableState.columns[index].name] = filter;
                  }
                });
                setNewFilters(newFilters);
              } else if (action === 'resetFilters') {
                setNewFilters([]);
                resetFilterApi();
              }
            },
          }}
          getData={(seletedPageSize, currentPageVal) => {
            setCurrentPage(currentPageVal);
            setPageSize(seletedPageSize);
          }}
        />
      </ThemeProvider>
      <FailFeedback
        open={error}
        title={t('mistake')}
        severity="warning"
        message={
          <>
            {duplicateRfids.length === 1 && (
              <Typography align="center">
                {t('LBLRFIDAlreadyExists', {
                  rfidTag: duplicateRfids[0].rfidTag,
                  rfidSN: duplicateRfids[0].serialNumber,
                })}
              </Typography>
            )}
            {duplicateRfids.length > 1 && (
              <>
                <Typography align="center" sx={{ mb: 4 }}>
                  {t('LBLRFIDAlreadyExistsMultiple')}
                </Typography>
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{ maxHeight: 220 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{t('LBLColumnRfidTag')}</TableCell>
                        <TableCell>{t('LBLColumnRfidSerialNr')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {duplicateRfids.map(({ rfidTag, serialNumber }, i) => (
                        <TableRow key={`${serialNumber}-${rfidTag}`}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{rfidTag}</TableCell>
                          <TableCell>{serialNumber}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
            {duplicateRfids.length === 0 && (
              <Typography align="center">
                {t('LBLSomethingwentWrong')}
              </Typography>
            )}
          </>
        }
        handleClose={() => {
          setError(false);
          setDuplicateRfids([]);
        }}
      />
      <Feedback
        open={success}
        title={t('LBLRFIDSuccess')}
        handleClose={() => {
          setSuccess(false);
        }}
      />
    </React.Fragment>
  );
}
export default RFIDS;
